import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { PageHeader } from "@/components/portal/PageHeader"
import { FilterBar } from "@/components/portal/FilterBar"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import {
  ART_ETIKETT, ART_GRUPPE, datumLang, tagMonat,
  type BetriebswissenAntwort, type Fakt, type FaktStatus,
} from "@/lib/betriebswissen"

/**
 * Wissensdatenbank je Betrieb (Paul, 01.10.2026: „… und als Extra-Reiter
 * ‚Wissensdatenbank' bei Intern").
 *
 * Fakten darüber, wie der Betrieb arbeitet — aus seinen eigenen Mails
 * gelernt und geprüft. Der Planer nutzt nur, was „gilt". Hier bestätigt oder
 * verwirft das Büro, was die Prüfung nicht allein entscheiden durfte.
 */

async function ladeWissen(tenant: string): Promise<BetriebswissenAntwort> {
  const r = await fetch(`/api/v1/betriebswissen?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function setzeStatus(id: number, status: "gilt" | "abgelehnt"): Promise<Fakt> {
  const r = await fetch(`/api/v1/betriebswissen/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

const STATUS_TEXT: Record<FaktStatus, string> = {
  gilt: "gilt",
  bestaetigen: "bitte bestätigen",
  vorgeschlagen: "gesammelt",
  abgelehnt: "abgelehnt",
}

const muted = { color: "var(--text-muted)" } as const

function FaktZeile({ f, aktionen }: { f: Fakt; aktionen?: React.ReactNode }) {
  const belege = f.belege?.length ?? 0
  return (
    <li data-testid={`fakt-${f.id}`}
        style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", alignItems: "flex-start",
                 padding: "10px 0", borderTop: "1px solid var(--border-light)" }}>
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.5 }}>
          <span className="ev2-meta-chip" style={{ fontSize: 11, padding: "1px 7px", marginRight: 6 }}>
            {ART_ETIKETT[f.art] || f.art}
          </span>
          {f.aussage}
        </div>
        <div style={{ fontSize: 12, marginTop: 3, ...muted }}>
          {f.bezug && <>{f.bezug} · </>}
          {belege} {belege === 1 ? "Beleg" : "Belege"}, {f.n_kunden} {f.n_kunden === 1 ? "Kunde" : "Kunden"}
          {f.verfallen
            ? <span style={{ color: "var(--warning-text)" }}> · verfallen</span>
            : f.verfaellt_am ? <> · gilt bis {tagMonat(f.verfaellt_am)}</> : null}
          {f.grund && <> · {f.grund}</>}
        </div>
      </div>
      {aktionen && <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{aktionen}</div>}
    </li>
  )
}

export default function WissensdatenbankPage() {
  const [tenant, setTenant] = useTenantFilter()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ["betriebswissen", tenant],
    queryFn: () => ladeWissen(tenant),
    enabled: !!tenant,
  })
  const entscheide = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "gilt" | "abgelehnt" }) => setzeStatus(id, status),
    onSuccess: (_d, v) => {
      toast(v.status === "gilt" ? "✓ Übernommen" : "✓ Als falsch markiert")
      qc.invalidateQueries({ queryKey: ["betriebswissen", tenant] })
      qc.invalidateQueries({ queryKey: ["betriebswissen-mail"] })
    },
    onError: (e) => toast(`Konnte nicht gespeichert werden: ${e instanceof Error ? e.message : String(e)}`, "error"),
  })

  const knopf = (f: Fakt, status: "gilt" | "abgelehnt", text: string, klasse = "") => (
    <button type="button" className={`pp-btn pp-btn-sm ${klasse}`.trim()}
            disabled={entscheide.isPending}
            onClick={() => entscheide.mutate({ id: f.id, status })}>
      {text}
    </button>
  )

  const fakten = q.data?.fakten ?? []
  const nach = (s: FaktStatus) => fakten.filter((f) => f.status === s)
  const bestaetigen = nach("bestaetigen")
  const gilt = nach("gilt")
  const vorgeschlagen = nach("vorgeschlagen")
  const abgelehnt = nach("abgelehnt")
  const lauf = q.data?.letzter_lauf

  return (
    <>
      <PageHeader title="Wissensdatenbank" />
      <FilterBar tenant={tenant} onTenantChange={setTenant} />
      {!tenant ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Bitte Betrieb wählen.
        </div>
      ) : q.isLoading ? <Loading label="Lade Wissensdatenbank…" /> :
        q.isError ? <ErrorBox error={q.error} /> :
        q.data ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 900 }}>
          <div className="pp-card" style={{ padding: 16 }} data-testid="wissen-kopf">
            <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Fakten darüber, wie dieser Betrieb arbeitet — aus seinen eigenen Mails gelernt und geprüft.
              Der Planer nutzt nur, was „gilt". Die Liste wird wöchentlich nachgeführt.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              {(["gilt", "bestaetigen", "vorgeschlagen", "abgelehnt"] as FaktStatus[]).map((s) => (
                <span key={s} className="ev2-meta-chip" data-testid={`zaehlung-${s}`}>
                  {q.data!.zaehlung?.[s] ?? 0} {STATUS_TEXT[s]}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 12, marginTop: 8, ...muted }}>
              {lauf
                ? <>Letzter Lauf: {datumLang(lauf.beendet_at || lauf.gestartet_at) || "—"}
                    {lauf.kosten_usd != null && <> · Kosten {Number(lauf.kosten_usd).toFixed(2)} $</>}</>
                : "Noch kein Lauf."}
            </div>
          </div>

          {bestaetigen.length > 0 && (
            <div className="pp-card" style={{ padding: 16 }} data-testid="abschnitt-bestaetigen">
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Bitte bestätigen ({bestaetigen.length})</div>
              <div style={{ fontSize: 12, ...muted }}>Stimmt das so? Erst dann nutzt der Planer es.</div>
              <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
                {bestaetigen.map((f) => (
                  <FaktZeile key={f.id} f={f} aktionen={<>
                    {knopf(f, "gilt", "Stimmt", "pp-btn-success")}
                    {knopf(f, "abgelehnt", "Stimmt nicht")}
                  </>} />
                ))}
              </ul>
            </div>
          )}

          <div className="pp-card" style={{ padding: 16 }} data-testid="abschnitt-gilt">
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Gilt ({gilt.length})</div>
            {gilt.length === 0 && <div style={{ fontSize: 12.5, ...muted }}>Noch nichts übernommen.</div>}
            {ART_GRUPPE.map(([art, titel]) => {
              const liste = gilt.filter((f) => f.art === art)
              if (!liste.length) return null
              return (
                <div key={art} style={{ marginTop: 12 }} data-testid={`gruppe-${art}`}>
                  <div className="ev2-meta-label" style={{ marginBottom: 2 }}>{titel}</div>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {liste.map((f) => (
                      <FaktZeile key={f.id} f={f} aktionen={knopf(f, "abgelehnt", "Stimmt nicht")} />
                    ))}
                  </ul>
                </div>
              )
            })}
            {/* Unbekannte Art (neue im Backend) nicht verschlucken. */}
            {(() => {
              const bekannt = new Set(ART_GRUPPE.map(([a]) => a))
              const rest = gilt.filter((f) => !bekannt.has(f.art))
              return rest.length ? (
                <div style={{ marginTop: 12 }}>
                  <div className="ev2-meta-label" style={{ marginBottom: 2 }}>Sonstiges</div>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {rest.map((f) => <FaktZeile key={f.id} f={f} aktionen={knopf(f, "abgelehnt", "Stimmt nicht")} />)}
                  </ul>
                </div>
              ) : null
            })()}
          </div>

          <details className="pp-card" style={{ padding: 16 }} data-testid="abschnitt-vorgeschlagen">
            <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              Gesammelt, noch unter der Schwelle ({vorgeschlagen.length})
            </summary>
            <div style={{ fontSize: 12, marginTop: 6, ...muted }}>
              Noch zu wenige Belege. Wird übernommen oder zur Bestätigung vorgelegt, sobald genug zusammenkommt.
            </div>
            <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
              {vorgeschlagen.map((f) => <FaktZeile key={f.id} f={f} />)}
            </ul>
          </details>

          <details className="pp-card" style={{ padding: 16 }} data-testid="abschnitt-abgelehnt">
            <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              Abgelehnt ({abgelehnt.length})
            </summary>
            <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
              {abgelehnt.map((f) => (
                <FaktZeile key={f.id} f={f} aktionen={knopf(f, "gilt", "Doch übernehmen")} />
              ))}
            </ul>
          </details>
        </div>
      ) : null}
    </>
  )
}
